// What an import's Apply does, record by record — the whole preview of both
// master imports (owner, 2026-10-20: "Let's focus this around the Changes
// listing."). Groups in reading order, each with its count, and a row per
// record: its title over a quieter second line, and on the right, in plain
// text, each change as label, old value struck through, new value — or a
// short note. No pills (owner, same day).

import { Body1Strong, Caption1, Caption1Strong, makeStyles, tokens } from '@fluentui/react-components'
import { ArrowRightRegular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  box: {
    maxHeight: '60vh',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  groupHead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 16px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  muted: { color: tokens.colorNeutralForeground3 },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
    columnGap: '24px',
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  title: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, overflowWrap: 'anywhere' },
  lines: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 },
  change: { display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', columnGap: '12px', alignItems: 'center' },
  values: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 },
  before: { color: tokens.colorNeutralForeground3, textDecorationLine: 'line-through' },
  arrow: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  more: { display: 'block', padding: '12px 16px', color: tokens.colorNeutralForeground3 },
})

/** One line of a row: a change from one value to another, or a note. */
export type ChangeLine = { label: string; before: string | null; after: string | null } | { note: string }

export interface ChangeRow {
  key: string
  title: string
  /** The quieter line under the title — a vendor code, a vehicle's make and model. */
  subtitle?: string | null
  lines: readonly ChangeLine[]
}

interface ChangeGroup {
  heading: string
  rows: readonly ChangeRow[]
  /** The true count, when `rows` is a sample of it. Defaults to `rows.length`. */
  total?: number
}

const count = (n: number) => n.toLocaleString('en-US')

export function ImportChangeList({ groups, empty }: { groups: readonly ChangeGroup[]; empty: string }) {
  const s = useStyles()
  const shown = groups.filter((g) => (g.total ?? g.rows.length) > 0)
  if (shown.length === 0) return <Body1Strong>{empty}</Body1Strong>
  return (
    <div className={s.box}>
      {shown.map((g) => {
        const total = g.total ?? g.rows.length
        return (
          <section key={g.heading} aria-label={g.heading}>
            <div className={s.groupHead}>
              <Caption1Strong>{g.heading}</Caption1Strong>
              <Caption1 className={s.muted}>{count(total)}</Caption1>
            </div>
            {g.rows.map((r) => (
              <div key={r.key} className={s.row}>
                <div className={s.title}>
                  <Body1Strong>{r.title}</Body1Strong>
                  {r.subtitle && <Caption1 className={s.muted}>{r.subtitle}</Caption1>}
                </div>
                <div className={s.lines}>
                  {r.lines.map((l, i) =>
                    'note' in l ? (
                      <Caption1 key={i} className={s.muted}>
                        {l.note}
                      </Caption1>
                    ) : (
                      <div key={i} className={s.change}>
                        <Caption1 className={s.muted}>{l.label}</Caption1>
                        <span className={s.values}>
                          <Caption1 className={s.before}>{l.before || 'blank'}</Caption1>
                          <ArrowRightRegular className={s.arrow} />
                          <Caption1Strong>{l.after || 'blank'}</Caption1Strong>
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
            {total > g.rows.length && <Caption1 className={s.more}>…and {count(total - g.rows.length)} more</Caption1>}
          </section>
        )
      })}
    </div>
  )
}
